import { useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useQueryable, vendorDashboardTenantsSurface, SURFACE_VENDOR_DASHBOARD_TENANTS } from '@/lib/queryable';
import { ViewModeSwitcher, SortPicker, FilterBar, SavedViewPicker } from '@/components/queryable';
import { TenantCard, type TenantCardData } from '@/components/portal/TenantCard';
import { EmptyTenantRelationships } from '@/components/portal/EmptyTenantRelationships';
import { VendorActivityFeed } from '@/components/portal/VendorActivityFeed';

function TenantTableRow({ tenant }: { tenant: TenantCardData }) {
  return (
    <tr className="border-b border-border-subtle hover:bg-bg-surface-2/50 transition-colors">
      <td className="py-3 px-4 text-sm font-medium text-text-primary">{tenant.name}</td>
      <td className="py-3 px-4 text-sm text-text-secondary">{tenant.slug}</td>
      <td className="py-3 px-4 text-sm text-text-secondary">
        {tenant.accepted_at ? new Date(tenant.accepted_at).toLocaleDateString() : '—'}
      </td>
      <td className="py-3 px-4 text-sm text-text-secondary">{tenant.role ?? '—'}</td>
    </tr>
  );
}

export default function PortalLandingPage() {
  const qbl = useQueryable<TenantCardData>({
    surface: vendorDashboardTenantsSurface,
    fetchUrl: '/portal/dashboard/tenants',
    savedViewsUrl: '/portal/views',
    syncToUrl: true,
  });

  const currentViewConfig = useMemo(() => ({
    sort: qbl.queryState.sort,
    filters: qbl.queryState.filters,
    viewMode: qbl.queryState.viewMode,
  }), [qbl.queryState]);

  const isCards = qbl.queryState.viewMode === 'cards';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-text-primary">Your Customer Relationships</h1>
        <p className="text-sm text-text-secondary mt-1">View the organizations you support, access assigned assessments, and manage security and compliance requests.</p>
      </div>

      <div className="flex gap-6">
        {/* Main tenant list */}
        <div className="flex-1 min-w-0 space-y-4">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder={vendorDashboardTenantsSurface.searchPlaceholder}
              value={qbl.queryState.search}
              onChange={(e) => qbl.setSearch(e.target.value)}
              className="h-8 w-56 text-sm"
            />
            <div className="flex items-center gap-2 ml-auto">
              <FilterBar
                filterOptions={vendorDashboardTenantsSurface.filterOptions}
                activeFilters={qbl.queryState.filters}
                onSetFilter={qbl.setFilter}
                onClearFilter={qbl.clearFilter}
                onClearAll={qbl.clearAllFilters}
              />
              <SortPicker
                options={vendorDashboardTenantsSurface.sortOptions}
                current={qbl.queryState.sort}
                onChange={qbl.setSort}
              />
              <SavedViewPicker
                surfaceKey={SURFACE_VENDOR_DASHBOARD_TENANTS}
                savedViews={qbl.savedViews}
                onLoad={qbl.loadSavedView}
                currentViewConfig={currentViewConfig}
              />
              <ViewModeSwitcher
                modes={vendorDashboardTenantsSurface.viewModes}
                current={qbl.queryState.viewMode}
                onChange={qbl.setViewMode}
              />
            </div>
          </div>

          {/* Loading */}
          {qbl.isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-28 rounded-lg" />
              ))}
            </div>
          )}

          {/* Empty state */}
          {!qbl.isLoading && qbl.data.length === 0 && (
            <EmptyTenantRelationships />
          )}

          {/* Cards view */}
          {!qbl.isLoading && qbl.data.length > 0 && isCards && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {qbl.data.map((t) => (
                <TenantCard key={t.tenant_id} tenant={t} />
              ))}
            </div>
          )}

          {/* Table view */}
          {!qbl.isLoading && qbl.data.length > 0 && !isCards && (
            <div className="rounded-lg border border-border-subtle overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-bg-surface-2 border-b border-border-subtle">
                  <tr>
                    <th className="py-2 px-4 text-xs font-medium text-text-secondary">Name</th>
                    <th className="py-2 px-4 text-xs font-medium text-text-secondary">Slug</th>
                    <th className="py-2 px-4 text-xs font-medium text-text-secondary">Connected</th>
                    <th className="py-2 px-4 text-xs font-medium text-text-secondary">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {qbl.data.map((t) => (
                    <TenantTableRow key={t.tenant_id} tenant={t} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {qbl.meta && qbl.meta.totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-text-secondary">
              <span>
                {qbl.meta.total} tenants — page {qbl.meta.page} of {qbl.meta.totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  disabled={!qbl.meta.hasPrev}
                  onClick={() => qbl.setPage(qbl.meta!.page - 1)}
                  className="text-sm disabled:opacity-40 hover:text-text-primary"
                >
                  ← Previous
                </button>
                <button
                  disabled={!qbl.meta.hasNext}
                  onClick={() => qbl.setPage(qbl.meta!.page + 1)}
                  className="text-sm disabled:opacity-40 hover:text-text-primary"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Activity feed sidebar (Q-OPEN-3) */}
        <aside className="w-64 shrink-0 hidden lg:block">
          <VendorActivityFeed />
        </aside>
      </div>
    </div>
  );
}
